import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from '../useAuth';
import { User, LicState } from "../types";
import { getUserProfile, updateUserProfile } from "../api/Users";
import { getAllLicStates } from "../api/States";
import { getAllProfessions, Profession } from "../api/Professions";
import { requestPasswordChange, confirmPasswordChange } from "../api/Auth";
import { getFilteredClasses } from "../api/Classes";
import { getFilteredCycles } from "../api/Cycles";
import { usePageTitle } from "../hooks/usePageTitle";

function Profile() {
  usePageTitle("My Profile");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState<User | null>(null);
  const [states, setStates] = useState<LicState[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Success/Error messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Stats
  const [stats, setStats] = useState({
    activeLicenses: 0,
    activeCycles: 0,
    totalClasses: 0,
    totalCredits: 0,
  });

  // Edit Profile Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  // Change Password Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Resend cooldown timer for password change
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const fetchData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const [profileData, statesData, professionsData] = await Promise.all([
        getUserProfile(user.id),
        getAllLicStates(),
        getAllProfessions(),
      ]);
      
      setProfile(profileData);
      setStates(statesData);
      setProfessions(professionsData);

      // Fetch stats
      const [classesData, cyclesData] = await Promise.all([
        getFilteredClasses({ limit: 1 }), // Just need stats
        getFilteredCycles({ limit: 1 }), // Just need stats
      ]);

      setStats({
        activeLicenses: profileData.licenses?.filter((lic: any) => lic.status === 'active').length || 0,
        activeCycles: cyclesData.stats?.activeCycles || 0,
        totalClasses: classesData.stats?.totalClasses || 0,
        totalCredits: classesData.stats?.totalCredits || 0,
      });

      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  // Edit Profile Handlers
  const handleOpenEdit = () => {
    if (profile) {
      setEditForm({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        phone: profile.phone || "",
        company: profile.company || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        zip: profile.zip || "",
      });
      setShowEditModal(true);
    }
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!profile) return;

    // Validation
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      setErrorMessage("First name and last name are required");
      return;
    }
    if (!editForm.email.trim()) {
      setErrorMessage("Email is required");
      return;
    }

    try {
      setEditLoading(true);
      const updateData: any = {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim(),
        phone: formatPhoneNumber(editForm.phone),
        company: editForm.company.trim(),
        address: editForm.address.trim(),
        city: editForm.city.trim(),
        state: editForm.state.trim(),
        zip: editForm.zip.trim(),
      };

      const updated = await updateUserProfile(profile.id, updateData);
      setProfile(updated);
      setSuccessMessage("Profile updated successfully");
      handleCloseEdit();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to update profile");
    } finally {
      setEditLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    // Remove all non-numeric characters for formatting
    const numeric = value.replace(/\D/g, '');
    let formatted = numeric;
    
    if (numeric.length > 0) {
      if (numeric.length <= 3) {
        formatted = `(${numeric}`;
      } else if (numeric.length <= 6) {
        formatted = `(${numeric.slice(0, 3)}) ${numeric.slice(3)}`;
      } else {
        formatted = `(${numeric.slice(0, 3)}) ${numeric.slice(3, 6)}-${numeric.slice(6, 10)}`;
      }
    }
    
    setEditForm({ ...editForm, phone: formatted });
  };

  const formatPhoneNumber = (phone: string): string => {
    const numeric = phone.replace(/\D/g, '');
    if (numeric.length === 10) {
      return `(${numeric.slice(0, 3)}) ${numeric.slice(3, 6)}-${numeric.slice(6, 10)}`;
    }
    return phone;
  };

  // Password Change Handlers
  const handleOpenPasswordModal = () => {
    setShowPasswordModal(true);
    setVerificationSent(false);
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setResendCooldown(0);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setVerificationSent(false);
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResendCooldown(0);
  };

  const handleRequestPasswordChange = async () => {
    if (!profile) return;
    try {
      setPasswordLoading(true);
      await requestPasswordChange();
      setVerificationSent(true);
      setResendCooldown(60); // Start 60-second cooldown
      setSuccessMessage("Verification code sent to your email");
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to send verification code");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleResendPasswordCode = async () => {
    if (!profile) return;
    setResending(true);
    try {
      await requestPasswordChange(); // Reuse same endpoint
      setSuccessMessage("A new verification code has been sent to your email");
      setResendCooldown(60); // Reset cooldown to 60 seconds
    } catch (err: any) {
      const message = err.response?.data?.error || err.response?.data?.message || "Failed to resend code";
      // Check if it's a rate limit error (429)
      if (err.response?.status === 429) {
        setErrorMessage("⚠️ " + message);
      } else {
        setErrorMessage(message);
      }
    } finally {
      setResending(false);
    }
  };

  const handleConfirmPasswordChange = async () => {
    if (!profile) return;

    // Validation
    if (verificationCode.length !== 6) {
      setErrorMessage("Verification code must be 6 digits");
      return;
    }
    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    try {
      setPasswordLoading(true);
      await confirmPasswordChange(verificationCode, newPassword);
      setSuccessMessage("Password changed successfully");
      handleClosePasswordModal();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Helper functions
  const getStateName = (stateID: number): string => {
    const state = states.find((s) => s.stateID === stateID);
    return state ? state.state : `State ${stateID}`;
  };

  const getProfessionName = (professionID: string): string => {
    const profession = professions.find((p) => p.professionID === professionID);
    return profession ? profession.name : professionID;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">My Profile</h2>
          <p className="text-muted mb-0">Manage your account information and preferences</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage("")}></button>
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage("")}></button>
        </div>
      )}

      {profile ? (
        <>
          <div className="row">
            {/* Profile Information Card */}
            <div className="col-lg-6 mb-4">
              <div className="card shadow-sm p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">Profile Information</h3>
                  <button className="btn btn-primary btn-sm" onClick={handleOpenEdit}>
                    Edit Profile
                  </button>
                </div>

                <div className="mt-3">
                  <div className="mb-3">
                    <strong className="text-muted d-block mb-1">Name</strong>
                    <span>{profile.firstName} {profile.lastName}</span>
                  </div>

                  <div className="mb-3">
                    <strong className="text-muted d-block mb-1">Email</strong>
                    <span>{profile.email}</span>
                  </div>

                  {profile.phone && (
                    <div className="mb-3">
                      <strong className="text-muted d-block mb-1">Phone</strong>
                      <span>{formatPhoneNumber(profile.phone)}</span>
                    </div>
                  )}

                  <div className="mb-3">
                    <strong className="text-muted d-block mb-1">Profession</strong>
                    <span>{profile.professionID ? getProfessionName(profile.professionID) : 'Not set'}</span>
                  </div>

                  {profile.company && (
                    <div className="mb-3">
                      <strong className="text-muted d-block mb-1">Company</strong>
                      <span>{profile.company}</span>
                    </div>
                  )}

                  {(profile.address || profile.city || profile.state || profile.zip) && (
                    <div className="mb-3">
                      <strong className="text-muted d-block mb-1">Address</strong>
                      <span>
                        {profile.address && <>{profile.address}<br /></>}
                        {(profile.city || profile.state || profile.zip) && (
                          <>
                            {profile.city}{profile.city && (profile.state || profile.zip) ? ', ' : ''}
                            {profile.state} {profile.zip}
                          </>
                        )}
                      </span>
                    </div>
                  )}

                  {profile.role === 'admin' && (
                    <div className="mb-3">
                      <strong className="text-muted d-block mb-1">Role</strong>
                      <span className="badge bg-primary">Admin</span>
                    </div>
                  )}

                  {(profile as any).createdAt && (
                    <div className="mb-3">
                      <strong className="text-muted d-block mb-1">Member Since</strong>
                      <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                        {new Date((profile as any).createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-top">
                    <button className="btn btn-outline-primary w-100" onClick={handleOpenPasswordModal}>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Summary Card */}
            <div className="col-lg-6 mb-4">
              <div className="card shadow-sm p-4 mb-4">
                <h3 className="mb-3">Account Summary</h3>

                <div className="row g-3">
                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="text-muted small mb-1">Active Licenses</div>
                      <div className="h4 mb-0">{stats.activeLicenses}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="text-muted small mb-1">Active Cycles</div>
                      <div className="h4 mb-0">{stats.activeCycles}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="text-muted small mb-1">Total Classes</div>
                      <div className="h4 mb-0">{stats.totalClasses}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="text-muted small mb-1">Total Credits</div>
                      <div className="h4 mb-0">{stats.totalCredits.toFixed(1)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan & Billing Card */}
              <div className="card shadow-sm p-4">
                <div className="mb-3">
                  <h3 className="mb-0">Plan & Billing</h3>
                </div>

                {(() => {
                  const sub = user?.subscription;
                  const plan = sub?.plan || 'free';
                  const tier = sub?.tier;
                  const interval = sub?.billingInterval;
                  const status = sub?.status;

                  const planLabel = plan === 'team' && tier
                    ? `Team — ${tier.charAt(0).toUpperCase() + tier.slice(1)}`
                    : plan === 'pro' ? 'Pro'
                    : 'Free';

                  const badgeClass = status === 'active' ? 'bg-success'
                    : status === 'past_due' ? 'bg-danger'
                    : status === 'trialing' ? 'bg-info'
                    : 'bg-secondary';

                  return (
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="fw-bold" style={{ fontSize: '1.1rem' }}>{planLabel}</span>
                        {status && status !== 'active' && (
                          <span className={`badge ${badgeClass}`}>{status}</span>
                        )}
                      </div>
                      {plan !== 'free' && interval && (
                        <div className="text-muted small mb-3">
                          {interval === 'annual' ? 'Annual billing' : 'Monthly billing'}
                          {interval === 'monthly' && (
                            <> · <Link to="/billing" style={{ color: 'var(--color-accent, #2ABFBF)' }}>Switch to annual &amp; save</Link></>
                          )}
                        </div>
                      )}
                      {plan === 'free' && (
                        <p className="text-muted small mb-3">
                          Upgrade to Pro for unlimited cycles, deadline alerts, and PDF exports.
                        </p>
                      )}
                      <Link to="/billing" className="btn btn-outline-secondary btn-sm w-100">
                        {plan === 'free' ? 'View plans & upgrade' : 'Billing & invoices'}
                      </Link>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-info">No profile information available.</div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Profile</h5>
                <button type="button" className="btn-close" onClick={handleCloseEdit}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label"><strong>First Name *</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Last Name *</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Email *</strong></label>
                  <input
                    type="email"
                    className="form-control"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Phone</strong></label>
                  <input
                    type="tel"
                    className="form-control"
                    value={editForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="(555) 555-5555"
                    maxLength={14}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Company</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                    placeholder="Company name"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Street Address</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="row">
                  <div className="col-md-5 mb-3">
                    <label className="form-label"><strong>City</strong></label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label"><strong>State</strong></label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      placeholder="State"
                      maxLength={2}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label"><strong>ZIP</strong></label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.zip}
                      onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })}
                      placeholder="12345"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseEdit}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success" 
                  onClick={handleSaveEdit}
                  disabled={editLoading}
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button type="button" className="btn-close" onClick={handleClosePasswordModal}></button>
              </div>
              <div className="modal-body">
                {!verificationSent ? (
                  <div>
                    <p>A verification code will be sent to your email address:</p>
                    <p className="mb-4"><strong>{profile?.email}</strong></p>
                    <button 
                      className="btn btn-primary w-100" 
                      onClick={handleRequestPasswordChange}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? 'Sending...' : 'Send Verification Code'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <label className="form-label">Verification Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        autoComplete="off"
                      />
                      <small className="text-muted d-block">Check your email for the verification code</small>
                      <small className="text-muted d-block">Code expires in 10 minutes</small>
                    </div>

                    {/* Resend code button with cooldown */}
                    <div className="text-center mb-3">
                      {resendCooldown > 0 ? (
                        <small className="text-muted">Resend code in {resendCooldown}s</small>
                      ) : (
                        <button 
                          type="button" 
                          className="btn btn-link btn-sm p-0" 
                          onClick={handleResendPasswordCode}
                          disabled={resending}
                        >
                          {resending ? 'Sending...' : "Didn't receive code? Resend"}
                        </button>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">New Password *</label>
                      <div className="input-group">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="form-control"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          minLength={8}
                          autoComplete="new-password"
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                      <small className="text-muted">Minimum 8 characters</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm New Password *</label>
                      <div className="input-group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          minLength={8}
                          autoComplete="new-password"
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    <button 
                      className="btn btn-success w-100" 
                      onClick={handleConfirmPasswordChange}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? 'Changing...' : 'Confirm Password Change'}
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClosePasswordModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
